import { useEffect, useState } from "react";

import { Button } from "../components/ui/button";
import { Badge, Input, Select, Table, Td, Th } from "../components/ui/misc";
import { api, type CatalogItemRow } from "../lib/api";

type Category = { id: string; code: string; name: string; parent_id: string | null };

const METHOD_LABEL: Record<string, string> = {
  area_factor: "按面积 × 系数",
  percent_of_slots: "按百分比",
  count_price: "按件",
};

/** 组件目录：顶层分类直接铺开，点分类看/改里面的组件，也支持手动新增。 */
export function Catalog() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<CatalogItemRow[]>([]);
  const [activeCode, setActiveCode] = useState<string>("cabinet");
  const [error, setError] = useState<string | null>(null);
  /** 停用的组件默认不出现（选料里也选不到）；勾上才能看到并放回来 */
  const [showInactive, setShowInactive] = useState(false);
  /** 部门（品牌）列表：新增组件时要指定归哪个品牌，列表里也要能看出归属 */
  const [departments, setDepartments] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [draft, setDraft] = useState({
    code: "",
    name: "",
    nameEn: "",
    unitPrice: "",
    calcMethod: "area_factor",
    unit: "m2",
    params: "{}",
    departmentId: "",
  });
  /** 正在编辑的组件（null = 没有）；编辑态直接改中英名/规格/算法/参数/单价/启用。 */
  const [editing, setEditing] = useState<{
    id: string;
    code: string;
    name: string;
    nameEn: string;
    calcMethod: string;
    unit: string;
    unitPrice: string;
    params: string;
    spec: string;
    active: boolean;
  } | null>(null);

  const load = async () => {
    try {
      const [categoryList, itemList, departmentList] = await Promise.all([
        api.categories(),
        api.catalogItems({ includeInactive: showInactive }),
        api.departments(),
      ]);
      setCategories(categoryList.categories.filter((entry) => entry.parent_id === null));
      setItems(itemList.items);
      setDepartments(departmentList.departments);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "加载失败");
    }
  };

  useEffect(() => {
    void load();
    // 勾选「显示已停用」要重新拉一次列表（停用的默认不返回）
  }, [showInactive]);

  // 二级分类（如 cabinet.particleboard）计入一级分类
  const inCategory = (item: CatalogItemRow, code: string) =>
    item.category_code === code || item.category_code.startsWith(`${code}.`);
  const visible = items.filter((item) => inCategory(item, activeCode));
  const activeCategory = categories.find((entry) => entry.code === activeCode);

  const savePrice = async (item: CatalogItemRow, value: string) => {
    const yuan = Number(value);
    if (!Number.isFinite(yuan) || yuan < 0) return;
    await api.updateCatalogItem(item.id, { unit_price_cents: Math.round(yuan * 100) });
    await load();
  };

  const startEdit = (item: CatalogItemRow) =>
    setEditing({
      id: item.id,
      code: item.code,
      name: item.name,
      nameEn: item.name_en ?? "",
      calcMethod: item.calc_method,
      unit: item.unit,
      unitPrice: (item.unit_price_cents / 100).toString(),
      params: item.params,
      spec: item.spec ?? "",
      active: item.active !== 0,
    });

  const saveEdit = async () => {
    if (!editing) return;
    try {
      let params: Record<string, unknown> = {};
      try {
        params = JSON.parse(editing.params) as Record<string, unknown>;
      } catch {
        setError("参数必须是合法 JSON");
        return;
      }
      await api.updateCatalogItem(editing.id, {
        name: editing.name,
        // 英文名给英文客户报价单用；清空就存 NULL（报价单会退回中文名）
        name_en: editing.nameEn.trim() || null,
        calc_method: editing.calcMethod,
        unit: editing.unit,
        unit_price_cents: Math.round(Number(editing.unitPrice || "0") * 100),
        params,
        spec: editing.spec || null,
        active: editing.active ? 1 : 0,
      });
      setEditing(null);
      setError(null);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "保存失败");
    }
  };

  const createItem = async () => {
    if (!activeCategory || !draft.code.trim() || !draft.name.trim()) return;
    try {
      let params: Record<string, unknown> = {};
      try {
        params = JSON.parse(draft.params) as Record<string, unknown>;
      } catch {
        params = {};
      }
      await api.createCatalogItem({
        category_id: activeCategory.id,
        code: draft.code.trim(),
        name: draft.name.trim(),
        name_en: draft.nameEn.trim() || null,
        calc_method: draft.calcMethod,
        unit: draft.unit,
        unit_price_cents: Math.round(Number(draft.unitPrice || "0") * 100),
        params,
        ...(draft.departmentId ? { department_id: draft.departmentId } : {}),
      });
      setDraft({
        code: "",
        name: "",
        nameEn: "",
        unitPrice: "",
        calcMethod: draft.calcMethod,
        unit: draft.unit,
        params: draft.params,
        departmentId: draft.departmentId,
      });
      await load();
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "新增失败");
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      {/* 分类直接铺开，不做搜索 */}
      <div className="grid grid-cols-6 gap-3">
        {categories.map((category) => {
          const count = items.filter((item) => inCategory(item, category.code)).length;
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveCode(category.code)}
              className={`rounded-xl border p-3 text-left transition ${
                category.code === activeCode
                  ? "border-[var(--color-primary)] bg-white shadow-sm"
                  : "border-[var(--color-border)] bg-white/60 hover:bg-white"
              }`}
            >
              <div className="text-sm font-medium">{category.name}</div>
              <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{count} 项</div>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-white">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-sm font-semibold">{activeCategory?.name ?? "组件"}</span>
          <span className="text-xs text-[var(--color-muted-foreground)]">
            直接改单价即可保存；名称下面那行英文名是给英文客户报价单用的，缺的会标出来
          </span>
          {/* 停用的组件选料时选不到，但得看得见——停错了要能放回来 */}
          <label className="flex cursor-pointer items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            显示已停用
          </label>
        </div>
        <div className="p-4">
          {error ? <p className="mb-2 text-sm text-[var(--color-destructive)]">{error}</p> : null}
          <Table>
            <thead>
              <tr>
                <Th>代码</Th>
                <Th>名称</Th>
                <Th>计价方式</Th>
                <Th>参数</Th>
                <Th className="w-32">单价（元）</Th>
                <Th className="w-24">操作</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) =>
                editing?.id === item.id ? (
                  <tr key={item.id} className="bg-[var(--color-muted)]">
                    <Td className="font-mono text-xs">{item.code}</Td>
                    <Td>
                      <Input
                        aria-label="名称"
                        value={editing.name}
                        onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                      />
                      <Input
                        aria-label="英文名"
                        className="mt-1"
                        placeholder="英文名（英文客户报价单用）"
                        value={editing.nameEn}
                        onChange={(event) => setEditing({ ...editing, nameEn: event.target.value })}
                      />
                      <Input
                        aria-label="规格"
                        className="mt-1"
                        placeholder="规格（可选）"
                        value={editing.spec}
                        onChange={(event) => setEditing({ ...editing, spec: event.target.value })}
                      />
                    </Td>
                    <Td>
                      <Select
                        aria-label="计价方式"
                        value={editing.calcMethod}
                        onChange={(event) => {
                          const calcMethod = event.target.value;
                          const unit =
                            calcMethod === "percent_of_slots"
                              ? "percent"
                              : calcMethod === "count_price"
                                ? "piece"
                                : "m2";
                          setEditing({ ...editing, calcMethod, unit });
                        }}
                      >
                        <option value="area_factor">按面积 × 系数</option>
                        <option value="percent_of_slots">按百分比</option>
                        <option value="count_price">按件</option>
                      </Select>
                      <Input
                        aria-label="单位"
                        className="mt-1 w-20"
                        value={editing.unit}
                        onChange={(event) => setEditing({ ...editing, unit: event.target.value })}
                      />
                    </Td>
                    <Td>
                      <Input
                        aria-label="参数"
                        className="font-mono text-xs"
                        value={editing.params}
                        onChange={(event) => setEditing({ ...editing, params: event.target.value })}
                      />
                    </Td>
                    <Td>
                      <Input
                        aria-label="单价"
                        type="number"
                        value={editing.unitPrice}
                        onChange={(event) => setEditing({ ...editing, unitPrice: event.target.value })}
                      />
                      <label className="mt-1 flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={editing.active}
                          onChange={(event) => setEditing({ ...editing, active: event.target.checked })}
                        />
                        启用
                      </label>
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" onClick={() => void saveEdit()}>
                          保存
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                          取消
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ) : (
                  <tr key={item.id} className={item.active === 0 ? "opacity-60" : undefined}>
                    <Td className="font-mono text-xs">{item.code}</Td>
                    <Td>
                      <div className="flex flex-col">
                        <span>
                          {item.name}
                          {/* 目录按部门隔离，标出这条属于哪个品牌 */}
                          <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                            {departments.find((department) => department.id === item.department_id)?.name ??
                              "全公司"}
                          </span>
                          {item.active === 0 ? (
                            <span className="ml-2 text-xs text-neutral-500">已停用（选料时选不到）</span>
                          ) : null}
                        </span>
                        {/*
                          英文名给英文客户报价单用：空着的话客户单子上会冒出中文，
                          所以缺的那几条直接点出来，方便一条条补。
                        */}
                        {item.name_en?.trim() ? (
                          <span className="text-xs text-[var(--color-muted-foreground)]">{item.name_en}</span>
                        ) : (
                          <span className="text-xs text-amber-700">缺英文名（英文报价单会显示中文）</span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <Badge tone="muted">{METHOD_LABEL[item.calc_method] ?? item.calc_method}</Badge>
                    </Td>
                    <Td className="font-mono text-xs text-[var(--color-muted-foreground)]">{item.params}</Td>
                    <Td>
                      {item.calc_method === "percent_of_slots" ? (
                        <span className="text-xs text-[var(--color-muted-foreground)]">按百分比</span>
                      ) : (
                        <Input
                          aria-label={`单价 ${item.code}`}
                          type="number"
                          defaultValue={(item.unit_price_cents / 100).toString()}
                          onBlur={(event) => void savePrice(item, event.target.value)}
                        />
                      )}
                    </Td>
                    <Td>
                      <Button variant="outline" size="sm" onClick={() => startEdit(item)}>
                        编辑
                      </Button>
                    </Td>
                  </tr>
                ),
              )}
              {visible.length === 0 ? (
                <tr>
                  <Td colSpan={6} className="text-center text-sm text-[var(--color-muted-foreground)]">
                    这个分类下还没有组件，用下面的表单加一个。
                  </Td>
                </tr>
              ) : null}
            </tbody>
          </Table>

          <div className="mt-4 grid grid-cols-12 gap-2 border-t border-[var(--color-border)] pt-4">
            <Input
              className="col-span-2"
              placeholder="代码"
              value={draft.code}
              onChange={(event) => setDraft({ ...draft, code: event.target.value })}
            />
            <Input
              className="col-span-3"
              placeholder="名称"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
            <Input
              className="col-span-3"
              placeholder="英文名（英文客户报价单用）"
              value={draft.nameEn}
              onChange={(event) => setDraft({ ...draft, nameEn: event.target.value })}
            />
            <Select
              className="col-span-2"
              aria-label="计价方式"
              value={draft.calcMethod}
              onChange={(event) => {
                const calcMethod = event.target.value;
                const unit =
                  calcMethod === "percent_of_slots"
                    ? "percent"
                    : calcMethod === "count_price"
                      ? "piece"
                      : "m2";
                setDraft({ ...draft, calcMethod, unit });
              }}
            >
              <option value="area_factor">按面积 × 系数</option>
              <option value="percent_of_slots">按百分比</option>
              <option value="count_price">按件</option>
            </Select>
            <Input
              className="col-span-1"
              placeholder="单位"
              value={draft.unit}
              onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
            />
            <Input
              className="col-span-1"
              placeholder="单价"
              value={draft.unitPrice}
              onChange={(event) => setDraft({ ...draft, unitPrice: event.target.value })}
            />
            <Input
              className="col-span-6 font-mono text-xs"
              placeholder='参数 JSON，如 {"factor":4}'
              value={draft.params}
              onChange={(event) => setDraft({ ...draft, params: event.target.value })}
            />
            <Select
              className="col-span-4"
              aria-label="归属部门"
              value={draft.departmentId}
              onChange={(event) => setDraft({ ...draft, departmentId: event.target.value })}
            >
              <option value="">归属：我的部门</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  归属：{department.name}
                </option>
              ))}
            </Select>
            <Button className="col-span-2" onClick={createItem}>
              新增
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
